/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Format predicates for the client drawer fields.
 *
 * Every predicate treats an empty value as valid: the drawer keeps almost all
 * fields optional, so "filled but malformed" is the only condition worth
 * reporting. Required-ness is expressed separately in the schema.
 */

import { isValidCNPJFormat, isValidCPFFormat } from '@/shared/utils/valide-formats';
import { BR_STATES } from '@/shared/utils/form-schemas';
import { onlyDigits } from '../../utils/masks';

/**
 * Digits in a valid Brazilian landline (10) or mobile (11) number.
 */
const PHONE_DIGIT_RANGE = { min: 10, max: 11 } as const;
/**
 * Digits in a Brazilian CEP.
 */
const CEP_DIGITS = 8;
/**
 * Instagram allows letters, digits, dots and underscores, up to 30 chars.
 */
const INSTAGRAM_HANDLE = /^@?[A-Za-z0-9._]{1,30}$/;
/**
 * Grouped currency as produced by `maskCurrency` (e.g. `1.250`). The optional
 * decimal tail is required for edit mode: `buildEditDefaultValues` formats the
 * stored income with `toLocaleString('pt-BR')`, which emits cents (`8.500,5`)
 * even though the masked input itself only ever produces whole units.
 */
const GROUPED_CURRENCY = /^(\d{1,3}(\.\d{3})*|\d+)(,\d{1,2})?$/;
/**
 * Structural e-mail check: one `@` and a dotted domain.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * `dd/mm/yyyy`, as produced by `maskDate`.
 */
const BR_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
/**
 * `yyyy-mm-dd`, as returned by the API for stored birth dates.
 */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * Oldest plausible birth date, guarding against typos like `1092`.
 */
const MAX_AGE_YEARS = 120;

/**
 * Whether the value is blank once trimmed.
 */
const isBlank = (value: string): boolean => !value || !value.trim();

/**
 * Wraps a predicate so blank values always pass.
 *
 * @param check - Predicate applied to non-blank values.
 * @returns A predicate that short-circuits to `true` on blank input.
 */
function whenFilled(check: (value: string) => boolean): (value: string) => boolean {
  return (value: string) => isBlank(value) || check(value);
}

export const isValidEmail = whenFilled((v) => EMAIL.test(v.trim()));

export const isValidPhone = whenFilled((v) => {
  const digits = onlyDigits(v).length;
  return digits >= PHONE_DIGIT_RANGE.min && digits <= PHONE_DIGIT_RANGE.max;
});

export const isValidInstagram = whenFilled((v) => INSTAGRAM_HANDLE.test(v.trim()));

export const isValidCpf = whenFilled((v) => isValidCPFFormat(v));

export const isValidCnpj = whenFilled((v) => isValidCNPJFormat(v));

export const isValidCep = whenFilled((v) => onlyDigits(v).length === CEP_DIGITS);

export const isValidBrState = whenFilled(
  (v) => (BR_STATES as readonly string[]).includes(v.trim().toUpperCase()),
);

export const isValidCurrency = whenFilled((v) => GROUPED_CURRENCY.test(v.trim()));

/**
 * Parses `dd/mm/yyyy` or `yyyy-mm-dd` into its calendar parts.
 *
 * @param value - The raw date text.
 * @returns The `[year, month, day]` triple, or null when unparseable.
 */
function parseDateParts(value: string): [number, number, number] | null {
  const br = BR_DATE.exec(value.trim());
  if (br) return [Number(br[3]), Number(br[2]), Number(br[1])];
  const iso = ISO_DATE.exec(value.trim());
  if (iso) return [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  return null;
}

/**
 * Whether month/day fall within the calendar's basic bounds.
 */
function isInCalendarRange(month: number, day: number): boolean {
  return month >= 1 && month <= 12 && day >= 1;
}

/**
 * Whether `date` round-trips back to the given year/month/day (rejects e.g. 31/02).
 */
function roundTripsTo(date: Date, year: number, month: number, day: number): boolean {
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * Whether the parts form a real calendar date (rejects 31/02, month 13, ...).
 *
 * @param year - The candidate year.
 * @param month - The candidate month (1-12).
 * @param day - The candidate day of month.
 * @returns Whether the triple is a real calendar date.
 */
function isRealDate(year: number, month: number, day: number): boolean {
  if (!isInCalendarRange(month, day)) return false;
  const date = new Date(year, month - 1, day);
  return roundTripsTo(date, year, month, day);
}

/**
 * Whether a birth date is a real calendar date within a plausible age range.
 * Blank passes; the drawer never requires a birth date.
 */
export const isValidBirthDate = whenFilled((value) => {
  const parts = parseDateParts(value);
  if (!parts) return false;
  const [year, month, day] = parts;
  if (!isRealDate(year, month, day)) return false;
  const date = new Date(year, month - 1, day);
  const now = new Date();
  if (date.getTime() > now.getTime()) return false;
  return year >= now.getFullYear() - MAX_AGE_YEARS;
});

/**
 * RG: 5-14 digits, optionally ending in the `X` check character.
 */
const RG = /^\d{5,13}X?$|^\d{5,14}$/;
/**
 * Issuing agency such as `SSP` or `SSP/SP`.
 */
const ISSUING_AGENCY = /^[A-Z]{2,10}(\/[A-Z]{2})?$/;
/**
 * Human/company names: must contain a letter; allows the usual punctuation.
 */
const NAME_TEXT = /^(?=.*[A-Za-zÀ-ÿ])[A-Za-zÀ-ÿ0-9\s.,'&/-]+$/;
/**
 * Free-form place text (street, neighborhood, complement).
 */
const PLACE_TEXT = /^(?=.*[A-Za-zÀ-ÿ0-9])[A-Za-zÀ-ÿ0-9\s.,'ºª°/-]+$/;
/**
 * Letters-only text such as nationality and profession. Parentheses and dots
 * are allowed because the app's own gender-neutral forms use them —
 * `Brasileiro(a)` is the default nationality written by the client mappers.
 */
const LETTERS_TEXT = /^[A-Za-zÀ-ÿ\s'().-]+$/;
/**
 * House number: digits with an optional letter suffix, or `S/N`.
 */
const ADDRESS_NUMBER = /^(\d{1,9}[A-Za-z]?|S\/?N)$/i;
/**
 * Shortest accepted length for the free-text place and name fields.
 */
const TEXT_MIN = 2;

/**
 * Whether the trimmed value reaches the shared minimum length.
 */
const hasMinLength = (value: string): boolean => value.trim().length >= TEXT_MIN;

export const isValidRg = whenFilled((v) => RG.test(v.replace(/[^0-9Xx]/g, '').toUpperCase()));

export const isValidIssuingAgency = whenFilled((v) => ISSUING_AGENCY.test(v.trim().toUpperCase()));

export const isValidNameText = whenFilled((v) => hasMinLength(v) && NAME_TEXT.test(v.trim()));

export const isValidPlaceText = whenFilled((v) => hasMinLength(v) && PLACE_TEXT.test(v.trim()));

export const isValidLettersText = whenFilled((v) => hasMinLength(v) && LETTERS_TEXT.test(v.trim()));

export const isValidAddressNumber = whenFilled((v) => ADDRESS_NUMBER.test(v.trim()));

/**
 * Picks the format predicate for a contact channel.
 *
 * @param type - Contact channel type.
 * @returns The predicate for that channel, or a blank-only check for unknowns.
 */
export function contactFormatCheck(type: string): (value: string) => boolean {
  if (type === 'email') return isValidEmail;
  if (type === 'phone' || type === 'whatsapp') return isValidPhone;
  if (type === 'instagram') return isValidInstagram;
  return () => true;
}
